import { useContext, useRef } from 'react';
import Autocomplete from 'react-google-autocomplete';
import AppContext from '../../contexts/AppContext';
import DataContext from '../../contexts/DataContext';
import { AddressOwnership } from '../enums';

interface GoogleAutocompleteProps {
  hasError: boolean;
  setHasError: (value: boolean) => void;
  setGoogleAutocompleteActive: (value: boolean) => void;
}

export default function GoogleAutocomplete({
  hasError,
  setHasError,
  setGoogleAutocompleteActive,
}: GoogleAutocompleteProps) {
  const { setShowClickToCall, setIsISR, isISRState } = useContext(AppContext);
  const { data, updateData, isDebrandedVersion } = useContext(DataContext);
  const autocompleteRef = useRef<any>(null);

  const handlePlaceSelected = (place: any) => {
    if (!place) {
      setShowClickToCall(false);
      setIsISR(false);
      return;
    }

    const { address_components, formatted_address } = place;

    if (autocompleteRef && autocompleteRef.current) {
      autocompleteRef.current.value = formatted_address;
    }

    // Address has error if no zip code is returned
    const postalCodeComponent = getGoogleAddressComponent(
      ['postal_code'],
      address_components
    );

    if (!postalCodeComponent) {
      setHasError(true);
    } else {
      const address = getNameFromGoogleAddressComponents(
        'address',
        address_components
      );

      // Address has error if it doesn't start with a house number
      if (!address.match(/[0-9].+/)) {
        setHasError(true);
        return;
      }
      setHasError(false);

      const city = getNameFromGoogleAddressComponents(
        'city',
        address_components
      );
      const state = getNameFromGoogleAddressComponents(
        'state',
        address_components
      );

      setShowClickToCall(isISRState(state));
      setIsISR(isISRState(state));

      const zipCode = getNameFromGoogleAddressComponents(
        'zipCode',
        address_components
      );

      const newAddressOwnership = data.addressOwnership || AddressOwnership.Own;

      updateData({
        ...data,
        addressOwnership: newAddressOwnership,
        selectedAddress: address,
        selectedCity: city,
        selectedState: state,
        selectedZipCode: zipCode,
        googleFormattedAddress: formatted_address,
        utilityProvider: null,
      });
    }
  };

  return (
    <>
      <Autocomplete
        ref={autocompleteRef}
        apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
        className={`mt-3 md:mt-4 short:!w-[350px] md:w-[400px] w-full text-[3c3c3c] placeholder:font-normal md-max:max-w-[279px] h-12 md:h-[57px] border border-[#3c3c3c] rounded-[60px] text-[15px] short:text-[15px] md:text-[17px] py-[18.5px] px-6 ${
          hasError ? 'border-1 border-red-600' : ''
        }`}
        defaultValue={data.googleFormattedAddress}
        onBlur={() => setGoogleAutocompleteActive(false)}
        onFocus={() => setGoogleAutocompleteActive(true)}
        onPlaceSelected={handlePlaceSelected}
        options={{
          types: ['address'],
          componentRestrictions: { country: 'us' },
        }}
        onChange={() => setHasError(true)}
        placeholder="123 Main St."
      />
      {hasError && (
        <p className="text-sm text-red-600 mt-2 pl-2">
          Please select your address from the drop-down list
        </p>
      )}
    </>
  );
}

const getGoogleAddressComponent = (types: string[], addressComponents: any) => {
  return addressComponents.find((component: any) =>
    types.some((type: string) => component.types.includes(type))
  );
};

const getNameFromGoogleAddressComponents = (
  type: 'address' | 'city' | 'state' | 'zipCode',
  addressComponents: any
): string => {
  let result = '';
  switch (type) {
    case 'address':
      const streetNumberComp = getGoogleAddressComponent(
        ['street_number'],
        addressComponents
      );
      const streetName = getGoogleAddressComponent(
        ['route'],
        addressComponents
      );
      result = `${streetNumberComp ? streetNumberComp.long_name + ' ' : ''}${
        streetName.short_name
      }`;
      break;
    case 'city':
      const cityComp = getGoogleAddressComponent(
        [
          'locality',
          'sublocality',
          'sublocality_level_1',
          'sublocality_level_2',
          'administrative_area_level_2',
        ],
        addressComponents
      );
      if (cityComp) result = cityComp.long_name;
      break;
    case 'state':
      const stateComp = getGoogleAddressComponent(
        ['administrative_area_level_1'],
        addressComponents
      );
      result = stateComp.short_name;
      break;
    case 'zipCode':
      const zipCodeComp = getGoogleAddressComponent(
        ['postal_code'],
        addressComponents
      );
      result = zipCodeComp.long_name;
      break;
    default:
      break;
  }

  return result;
};
